/**
 * Utilities for manipulating/validating date related data
 *
 * @module lib/DateUtils
 */
import isInteger from 'lodash/isInteger';
import isNumber from 'lodash/isNumber';

import { getCountryCode } from '../lib/configuration/TunerVarUtils';

export const MIN_AGE_YEARS_US = 13;

export const MIN_AGES_DICT = {
  US: MIN_AGE_YEARS_US,
};

/**
 * Return the minimum age for country code
 *
 * @param {string} countryCode - one of CountryCodes
 * @returns {number} min age
 */
function getMinAgeForCountryCode(countryCode: keyof typeof MIN_AGES_DICT | string): number {
  return MIN_AGES_DICT[countryCode as keyof typeof MIN_AGES_DICT] ?? 0;
}

/**
 * Calculates age based on exact birth date
 *
 * @param {Date} birthDate - date obj
 * @returns {number} - age as of now
 */
function getAge(birthDate: Date): number {
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

/**
 * Util for creating a new Date with no params for test stubbing purposes
 *
 * @returns {Date} - new date object
 */
export function getDate(): Date {
  return new Date();
}

/**
 * Does it meet minimum age
 *
 * @param {number} year - year to check
 * @param {string} countryCode - country code
 * @returns {boolean} - valid or not
 */
export function isOldEnoughBirthYear(year: number, countryCode: string): boolean {
  const minAge = getMinAgeForCountryCode(countryCode);
  const sanitized = isNumber(year) ? year : parseInt(year, 10);
  const thisYear = new Date().getFullYear();
  return isNumber(sanitized) && thisYear - sanitized > minAge;
}

/**
 * Is birth year at minimum age?
 *
 * @param {number} year - year to check
 * @param {string} countryCode - country code
 * @returns {boolean} - valid or not
 */
export function isBirthYearOnTheCuspForCountry(year: number, countryCode: string): boolean {
  const minAge = getMinAgeForCountryCode(countryCode);
  const sanitized = isNumber(year) ? year : parseInt(year, 10);
  const thisYear = new Date().getFullYear();

  return isNumber(sanitized) && thisYear - sanitized === minAge;
}

/**
 * Turn year/month/day into js date object
 *
 * @param {number} year - year of returned date object
 * @param {number} month - month of returned date object
 * @param {number} day - day of returned date object
 * @returns {Date} - js date object
 */
export function parseDate(year: number, month = 0, day = 1): Date {
  const y = isNumber(year) ? year : parseInt(year, 10);
  const m = isNumber(month) ? month : parseInt(month, 10);
  const d = isNumber(day) ? day : parseInt(day, 10);
  return new Date(y, isInteger(m) ? m : 0, isInteger(d) ? d : 1);
}

/**
 * Convert string date to date object.
 *
 * @param {string} dateString - ISO 8601 timestamp (e.g. 2019-05-04T19:00:00)
 * @returns {Date} - date object
 */
export function parseDateString(dateString: string): Date {
  const year = parseInt(dateString.slice(0, 4), 10);
  const month = parseInt(dateString.slice(5, 7), 10) - 1; // months start at 0
  const day = parseInt(dateString.slice(8, 10), 10);
  const hour = parseInt(dateString.slice(11, 13), 10);
  const minute = parseInt(dateString.slice(14, 16), 10);
  const second = parseInt(dateString.slice(17, 19), 10);
  return new Date(year, month, day, hour, minute, second);
}

/**
 * Does it meet minimum age to the date?
 *
 * @param {number} year - year to check
 * @param {number} month - month to check. pos based (1 = jan, 2 = feb, etc)
 * @param {number} day - day to check
 * @param {object} state - redux state
 * @returns {boolean} - valid or not
 */
export function isOldEnoughBirthDate(
  year: number,
  month: number,
  day: number,
  state: $TSFixMe,
): boolean {
  const birth = parseDate(year, month - 1, day);
  const age = getAge(birth);
  return age >= getMinAgeForCountryCode(getCountryCode(state));
}

export function isUnderage(state: $TSFixMe, year: number, month: number, day: number): boolean {
  const countryCode = getCountryCode(state);
  if (isOldEnoughBirthYear(year, countryCode)) {
    return false;
  }
  return !isOldEnoughBirthDate(year, month, day, state);
}

export function isUnderageFamilyPlan(birthYear: number): boolean {
  const minAge = 21;
  const thisYear = new Date().getFullYear();
  const result = isNumber(birthYear) && thisYear - birthYear < minAge;
  return result;
}

function padNumber(number: number): string | number {
  if (number < 10) {
    return '0' + number;
  }
  return number;
}

export function formatDate(date: Date): string {
  return (
    date.getFullYear() +
    '-' +
    padNumber(date.getMonth() + 1) +
    '-' +
    padNumber(date.getDate()) +
    ' ' +
    padNumber(date.getHours()) +
    ':' +
    padNumber(date.getMinutes()) +
    ':' +
    padNumber(date.getSeconds())
  );
}

export function formatStatsDayDate(date: Date): string {
  return (
    date.getUTCFullYear() + '-' + padNumber(date.getMonth() + 1) + '-' + padNumber(date.getDate())
  );
}

/**
 * Converts date object into YYYY-MM-DD string
 *
 * @param {Date} date - date to format, if not set then a date is generated
 * @returns {string} - formatted string
 */
export function formatIsoDate(date: Date): string {
  const dateToFormat = date || new Date();
  return dateToFormat.toISOString().substring(0, 10);
}

/**
 *
 * @param {Date} date
 * @returns {boolean}
 */
export function isValidDate(date: Date): boolean {
  return !!(date && date.valueOf && Number.isFinite(date.valueOf()));
}

export function isValidNonZeroInteger(num: number): boolean {
  return Number.isInteger(num) && num > 0;
}

export function getDaysInMonth(month: number, year: number): number {
  if (!isValidNonZeroInteger(month) || !isValidNonZeroInteger(year)) {
    return NaN;
  }
  // month is not zero indexed. january = 1, feb = 2, etc.
  switch (month) {
    case 2:
      // Feb, + math for leap year calculation.
      return (year % 4 === 0 && year % 100) || year % 400 === 0 ? 29 : 28;
    case 4:
    case 6:
    case 9:
    case 11:
      // April, June, Sep, Nov
      return 30;
    case 1:
    case 3:
    case 5:
    case 7:
    case 8:
    case 10:
    case 12:
      // Jan, Mar, May, July, Aug, Dec
      return 31;
    default:
      // all invalid months
      return NaN;
  }
}

export function isRealDate(month: number, day: number, year: number): boolean {
  // month is not zero indexed. january = 1, feb = 2, etc.
  if (
    !isValidNonZeroInteger(month) ||
    !isValidNonZeroInteger(day) ||
    !isValidNonZeroInteger(year)
  ) {
    return false;
  }
  return month <= 12 && day <= getDaysInMonth(month, year);
}
